import React, { useMemo } from 'react';
import { TableInstance } from 'react-table';

import { Calculation } from '@work4all/models/lib/Classes/Calculation.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EntityTable, IEntityTable } from './entity-table/EntityTable';
import { useEntityDataTable } from './entity-table/use-entity-data-table';
import { ErpRessourceClassCell } from './ErpRessourceClassCell';
import { useCloneConvertTableErpActions } from './offers-table/hooks/use-clone-convert-erp';
import { useErpPlanningColumns } from './planning-columns/use-erp-planning-columns';
import schema from './schemata/calculation-table-schema.json';
import { useErpHandlersTemplate } from './use-erp-handlers-template';

const defaultSort = [
  { field: 'date', direction: SortDirection.DESCENDING },
  { field: 'number', direction: SortDirection.DESCENDING },
];

export const CalculationsTable = React.forwardRef<TableInstance, IEntityTable>(
  function CalculationsTable(_props, ref) {
    const ressourceClassColumns = useErpPlanningColumns({
      entity: Entities.calculation,
    });

    const transformedSchema = useMemo(() => {
      return {
        ...schema,
        columns: [...schema.columns, ...ressourceClassColumns],
      } as never;
    }, [ressourceClassColumns]);

    const { ...dataTable } = useEntityDataTable<Calculation, never>({
      schema: transformedSchema,
      cells: {
        ErpRessourceClass: ErpRessourceClassCell,
      },
      defaultSort,
      forceRequestFields: {
        positionList: [
          {
            id: null,
            unit: null,
            amount: null,
            article: {
              id: null,
              name: null,
              number: null,
              ressourceClassId: null,
            },
          },
        ],
      } as Calculation,
      rowModifiers: (value) => {
        return {
          isFaded: value.isClosed && value.frozen,
          isShade1: !value.isClosed && value.frozen,
        };
      },
    });

    const template = useErpHandlersTemplate();

    const { actions } = useCloneConvertTableErpActions({
      entity: Entities.calculation,
    });

    const custom = useMemo(
      () => ({
        left: actions,
      }),
      [actions]
    );

    return (
      <EntityTable
        ref={ref}
        template={template}
        {...dataTable}
        {..._props}
        actions={{
          custom,
        }}
      />
    );
  }
);
